import { ReactNode, useEffect } from 'react';
import { usePage } from '@inertiajs/react';
import MainProvider from '@/Logigate/MainProvider';
import { EstruturaEscola } from '@/Escola/estrutura/EstruturaEscola';
import { EstruturaLogigate } from '@/Logigate/estrutura/EstruturaLogigate';

/**
 * O layout das páginas do painel do Logigate e da Escola.
 *
 * O mesmo ficheiro de página corre nos dois painéis: no da Escola (`props.context ===
 * 'escola'`, em `{slug}.escola.ao`) e dentro do Logigate (`/{empresa}/escola-inteligente`).
 * O `CascoDoPainel` escolhe o casco certo — os dois são o `EstruturaPainel` do kit, em
 * Tailwind, com o menu lateral que expande e minimiza.
 *
 * - Páginas ainda em Mantine: `X.layout = (p) => <UserLayout>{p}</UserLayout>`. O
 *   `UserLayout` põe-nas dentro de um `Legado`, que repõe o espaço e as cores de antes.
 * - Páginas já em Tailwind: `X.layout = layoutPainel`. O mesmo casco, sem o `Legado`.
 *
 * Os dois devolvem o `CascoDoPainel` na raiz, por isso passar de uma página antiga para uma
 * nova não desmonta o menu (o React reaproveita-o).
 */

interface Props {
    children: ReactNode;
    /**
     * Tira as margens do miolo, para uma página que ocupa o ecrã.
     *
     * ⚠️ Existe por causa das aplicações do Honga Apps: uma página que declara
     * `altura: cheia` no manifesto desenhava-se dentro das margens do layout, portanto
     * "ecrã inteiro" ficava a ser um rectângulo com margens de todos os lados.
     */
    semMargens?: boolean;
}

export function WithUserLayout(WrappedComponent: any) {
    return (props: any) => {
        const { component: Component, props: inertiaProps } = usePage().props;
        return (
            <UserLayout>
                <WrappedComponent {...props} {...inertiaProps} component={Component} />
            </UserLayout>
        );
    };
}

export const BlankLayout = ({ children }: Props) => {
    return <MainProvider>{children}</MainProvider>;
};

/** O casco do painel certo para o contexto (Escola ou Logigate). */
export function CascoDoPainel({ children, semMargens = false }: Props) {
    const { props } = usePage();
    const basePath = props.context === 'escola' ? null : ((props as any).base_path as string | undefined);

    // A base dos uploads temporários (ver CustomUpload.ts): só dentro de uma
    // empresa, e só enquanto este layout estiver montado.
    useEffect(() => {
        if (typeof basePath !== 'string' || !basePath) return;
        (window as any).__hongaUploadBase = basePath;
        return () => {
            if ((window as any).__hongaUploadBase === basePath) {
                delete (window as any).__hongaUploadBase;
            }
        };
    }, [basePath]);

    if (props.context === 'escola') {
        return <EstruturaEscola semMargens={semMargens}>{children}</EstruturaEscola>;
    }
    return <EstruturaLogigate semMargens={semMargens}>{children}</EstruturaLogigate>;
}

/**
 * À volta de uma página que ainda é Mantine: as classes `escola-legado` /
 * `logigate-legado`, para as encontrar. As páginas novas levam `data-pagina-tailwind`, que
 * desliga as cores antigas da Escola (ver `resources/css/escola-legado.css`).
 */
function Legado({ children }: { children: ReactNode }) {
    const { props } = usePage();
    // `overflow-x-auto`: no casco antigo era o `main` que deslizava; aqui é o documento, e uma
    // página larga alargava-o (no telemóvel o browser afastava o zoom e nada se acertava).
    return <div className={`${props.context === 'escola' ? 'escola-legado' : 'logigate-legado'} min-w-0 overflow-x-auto`}>{children}</div>;
}

/**
 * O layout das páginas do painel. Por omissão é o das páginas antigas (Mantine, com o
 * `Legado` à volta); as novas usam `layoutPainel`, que é este mesmo com `legado={false}` —
 * a raiz é sempre o `UserLayout`, e o React não desmonta o menu entre umas e outras.
 */
export const UserLayout = ({ children, semMargens = false, legado = true }: Props & { legado?: boolean }) => (
    <CascoDoPainel semMargens={semMargens}>
        {legado && !semMargens ? <Legado>{children}</Legado> : legado ? children : <div data-pagina-tailwind className="contents">{children}</div>}
    </CascoDoPainel>
);

/** O layout das páginas novas (Tailwind): `Pagina.layout = layoutPainel`. */
export const layoutPainel = (pagina: ReactNode) => <UserLayout legado={false}>{pagina}</UserLayout>;
